import Bugsnag from '@bugsnag/js'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material'
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import { MdNavigateBefore, MdNavigateNext } from 'react-icons/md'
import { useLocation, useNavigate } from 'react-router-dom'
import { FormType } from '../utils/sharedTypes'
import { client } from '../utils/client'
import { startDocusignSignaturePolling } from '../utils/docusignPolling'

type GoCardlessStatus = {
  contract_signed: boolean
  mandate_status: string | null
  mandate_ready: boolean
  mandate_bank_name?: string | null
  mandate_account_number_ending?: string | null
  card_registered: boolean
}

type GoCardlessPropsType = {
  form: FormType
  setForm: Dispatch<SetStateAction<FormType>>
}

const STEPS = ['Signature du contrat', 'Mandat SEPA']

// First envelope creation is slow (4 PDFs generated then DocuSign API), so use a generous timeout
// and a few retries instead of failing on the first slow response.
const DOCUSIGN_ENVELOPE_TIMEOUT_MS = 90_000
const DOCUSIGN_LOAD_MAX_ATTEMPTS = 5
const DOCUSIGN_LOAD_RETRY_BASE_MS = 2000
const DOCUSIGN_LOADING_MESSAGE_INTERVAL_MS = 5000
const DOCUSIGN_CRM_STATUS_POLL_MS = 15_000
const DOCUSIGN_LOADING_MESSAGES = [
  'Préparation de vos documents...',
  'Génération du devis et de l’échéancier...',
  'Génération du contrat de formation...',
  'Connexion à la plateforme de signature...',
  "Création de l'enveloppe électronique...",
  'Encore un instant, merci de patienter...',
]

const MANDATE_CONFIRM_POLL_MS = 2000
const MANDATE_CONFIRM_TIMEOUT_MS = 30_000

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/** Iframe height: fixed viewport band so the page does not scroll over the DocuSign panel. */
const DOCUSIGN_IFRAME_HEIGHT = {
  xs: 'min(560px, calc(100dvh - 300px))',
  sm: 'min(640px, calc(100dvh - 260px))',
} as const

const GoCardless = ({ form }: GoCardlessPropsType) => {
  const navigate = useNavigate()
  const location = useLocation()
  const globalId = form.data.global_id

  const [status, setStatus] = useState<GoCardlessStatus | null>(null)
  const [docusignUrl, setDocusignUrl] = useState<string>('')
  const [docusignLoading, setDocusignLoading] = useState(true)
  const [docusignLoadFailed, setDocusignLoadFailed] = useState(false)
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mandateConfirming, setMandateConfirming] = useState(false)
  const [mandateConfirmTimedOut, setMandateConfirmTimedOut] = useState(false)

  const fetchStatus = useCallback(async () => {
    if (!globalId) return
    const resp = await client.get(`/registration/${globalId}/gocardless_status/`)
    setStatus(resp.data as GoCardlessStatus)
  }, [globalId])

  const loadDocusignEnvelope = useCallback(async () => {
    if (!globalId) return
    setDocusignLoading(true)
    setDocusignLoadFailed(false)

    for (let attempt = 0; attempt < DOCUSIGN_LOAD_MAX_ATTEMPTS; attempt += 1) {
      try {
        const resp = await client.get(`/registration/${globalId}/get_or_create_gocardless_docusign_envelope/`, {
          timeout: DOCUSIGN_ENVELOPE_TIMEOUT_MS,
        })
        const url = (resp.data as string) || ''
        if (url) {
          setDocusignUrl(url)
          setDocusignLoading(false)
          return
        }
      } catch (err) {
        if (attempt === DOCUSIGN_LOAD_MAX_ATTEMPTS - 1) {
          Bugsnag.notify(err as Error)
        }
      }
      if (attempt < DOCUSIGN_LOAD_MAX_ATTEMPTS - 1) {
        await sleep(DOCUSIGN_LOAD_RETRY_BASE_MS * (attempt + 1))
      }
    }

    setDocusignUrl('')
    setDocusignLoadFailed(true)
    setDocusignLoading(false)
  }, [globalId])

  // 0 = contract not signed, 1 = mandate not ready (no external_id yet), 2 = mandate confirmed
  const activeStep = !status ? 0 : !status.contract_signed ? 0 : !status.mandate_ready ? 1 : 2
  const returningFromMandateFlow = new URLSearchParams(location.search).get('step') === 'card'

  useEffect(() => {
    const incomingError = (location.state as { gcError?: string } | null)?.gcError
    if (incomingError) {
      setError(incomingError)
    }
  }, [location.state])

  useEffect(() => {
    if (!globalId) {
      setError('Identifiant manquant')
      setLoading(false)
      return
    }
    fetchStatus()
      .catch((err: Error) => {
        Bugsnag.notify(err)
        setError("Impossible de charger l'inscription")
      })
      .finally(() => setLoading(false))
  }, [globalId, fetchStatus])

  useEffect(() => {
    if (activeStep === 0 && status && !status.contract_signed && !docusignUrl && !docusignLoadFailed) {
      loadDocusignEnvelope().catch((err: Error) => Bugsnag.notify(err))
    }
  }, [activeStep, status, docusignUrl, docusignLoadFailed, loadDocusignEnvelope])

  // Rotate the reassuring loading messages while the envelope is being prepared
  useEffect(() => {
    if (!docusignLoading) {
      setLoadingMessageIndex(0)
      return undefined
    }
    const intervalId = setInterval(() => {
      setLoadingMessageIndex((prev) => Math.min(prev + 1, DOCUSIGN_LOADING_MESSAGES.length - 1))
    }, DOCUSIGN_LOADING_MESSAGE_INTERVAL_MS)
    return () => clearInterval(intervalId)
  }, [docusignLoading])

  // Finalize the contract signature from the parent page. In local dev the embedded iframe cannot
  // redirect to a localhost return URL (Private Network Access is blocked), so the postMessage never
  // fires; this also acts as a safety net in production if the message is missed.
  const finalizeContractSignature = useCallback(
    async (force = false): Promise<boolean> => {
      if (!globalId) return false
      const resp = await client.post(`/registration/${globalId}/sign_gocardless_docusign/`, undefined, {
        params: force ? { force: '1' } : undefined,
      })
      const data = resp.data as { signed: boolean }
      if (data.signed) {
        await fetchStatus()
      }
      return data.signed
    },
    [globalId, fetchStatus],
  )

  // Refresh after DocuSign embedded signing completes (postMessage from the return URL page)
  useEffect(() => {
    const listener = (event: MessageEvent) => {
      if (event.data === 'DOCUSIGN_SIGNED_REFRESH') {
        finalizeContractSignature(true).catch((err: Error) => Bugsnag.notify(err))
      }
    }
    window.addEventListener('message', listener)
    return () => window.removeEventListener('message', listener)
  }, [finalizeContractSignature])

  // Light CRM polling (no DocuSign API) while the iframe is open.
  useEffect(() => {
    if (!globalId || !docusignUrl || status?.contract_signed) return undefined
    const intervalId = setInterval(() => {
      if (document.hidden) return
      fetchStatus().catch((err: Error) => Bugsnag.notify(err))
    }, DOCUSIGN_CRM_STATUS_POLL_MS)
    return () => clearInterval(intervalId)
  }, [globalId, docusignUrl, status?.contract_signed, fetchStatus])

  // DocuSign sync while the iframe is open (PNA / missed postMessage fallback). Fast for 5 min, then slower.
  useEffect(() => {
    if (!globalId || !docusignUrl || status?.contract_signed) return undefined

    let stopped = false
    const stopPolling = startDocusignSignaturePolling(() => {
      if (stopped) return
      return finalizeContractSignature().then((signed) => {
        if (signed) {
          stopped = true
          stopPolling()
        }
      })
    })

    const onVisibilityChange = () => {
      if (!document.hidden && !stopped) {
        finalizeContractSignature(true).catch((err: Error) => Bugsnag.notify(err))
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      stopped = true
      stopPolling()
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [globalId, docusignUrl, status?.contract_signed, finalizeContractSignature])

  useEffect(() => {
    if (status?.mandate_ready) {
      setMandateConfirming(false)
      setMandateConfirmTimedOut(false)
    }
  }, [status?.mandate_ready])

  // After GoCardless redirect: poll until webhook attaches external_id (mandate_ready).
  useEffect(() => {
    if (!returningFromMandateFlow || !status?.contract_signed || status.mandate_ready || mandateConfirmTimedOut) {
      return undefined
    }

    setMandateConfirming(true)
    const startedAt = Date.now()
    const interval = setInterval(() => {
      if (Date.now() - startedAt >= MANDATE_CONFIRM_TIMEOUT_MS) {
        clearInterval(interval)
        setMandateConfirming(false)
        setMandateConfirmTimedOut(true)
        return
      }
      fetchStatus().catch((err: Error) => Bugsnag.notify(err))
    }, MANDATE_CONFIRM_POLL_MS)

    return () => clearInterval(interval)
  }, [
    returningFromMandateFlow,
    status?.contract_signed,
    status?.mandate_ready,
    mandateConfirmTimedOut,
    fetchStatus,
  ])

  const startMandateFlow = async () => {
    if (!globalId) return
    setActionLoading(true)
    setMandateConfirmTimedOut(false)
    setError(null)
    try {
      const resp = await client.post(`/registration/${globalId}/create_gocardless_mandate_flow/`)
      const data = resp.data as { redirect_url: string | null }
      if (data.redirect_url) {
        window.location.href = data.redirect_url
      } else {
        setError('Impossible de démarrer la signature du mandat SEPA')
      }
    } catch (err) {
      Bugsnag.notify(err as Error)
      setError('Une erreur est survenue lors de la création du mandat SEPA')
    } finally {
      setActionLoading(false)
    }
  }

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: 240 }}>
        <CircularProgress />
      </Grid>
    )
  }

  const isDocusignStep = activeStep === 0

  const renderDocusignPanel = () => {
    if (docusignUrl) {
      return (
        <iframe
          src={docusignUrl}
          title="Signature DocuSign"
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
            border: 'none',
          }}
        />
      )
    }
    if (docusignLoadFailed) {
      return (
        <Grid container direction="column" justifyContent="center" alignItems="center" sx={{ height: '100%', p: 3 }}>
          <Typography sx={{ mb: 2, textAlign: 'center' }}>
            Le chargement de vos documents prend plus de temps que prévu.
          </Typography>
          <Button variant="outlined" onClick={() => void loadDocusignEnvelope()}>
            Réessayer
          </Button>
        </Grid>
      )
    }
    return (
      <Grid container direction="column" justifyContent="center" alignItems="center" sx={{ height: '100%', p: 3 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2, textAlign: 'center' }}>{DOCUSIGN_LOADING_MESSAGES[loadingMessageIndex]}</Typography>
      </Grid>
    )
  }

  const renderMandateStep = () => {
    const showConfirming =
      returningFromMandateFlow && !mandateConfirmTimedOut && (mandateConfirming || !status?.mandate_ready)
    if (showConfirming) {
      return (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            Confirmation de votre mandat SEPA…
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Nous attendons la confirmation de votre banque. Cela ne prend généralement que quelques secondes.
          </Typography>
        </Paper>
      )
    }

    if (mandateConfirmTimedOut) {
      return (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Confirmation du mandat en attente
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Nous n&apos;avons pas encore reçu la confirmation de votre mandat SEPA. Vous pouvez réessayer la mise en
            place du mandat, ou patienter encore un instant puis actualiser la page.
          </Typography>
          <Button variant="contained" onClick={() => void startMandateFlow()} disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={20} /> : 'Réessayer le mandat SEPA'}
          </Button>
        </Paper>
      )
    }

    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Mise en place du mandat de prélèvement SEPA
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Vous allez être redirigé vers notre prestataire GoCardless pour autoriser les prélèvements SEPA de vos
          échéances.
        </Typography>
        <Button variant="contained" onClick={() => void startMandateFlow()} disabled={actionLoading}>
          {actionLoading ? <CircularProgress size={20} /> : 'Mettre en place le mandat SEPA'}
        </Button>
      </Paper>
    )
  }

  return (
    <Box
      component="section"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        maxWidth: '100%',
      }}
    >
      <Typography variant="body2" color="text.secondary">
        Signez votre contrat, mettez en place votre mandat de prélèvement SEPA puis enregistrez votre carte bancaire pour
        finaliser votre financement. Votre premier versement sera prélevé par carte à la première échéance, les échéances
        suivantes par prélèvement SEPA.
      </Typography>

      {/* Progress: compact on DocuSign step to keep the iframe panel unobstructed */}
      <Box sx={{ flexShrink: 0, width: '100%' }}>
        {isDocusignStep ? (
          <Typography variant="subtitle2" color="text.secondary">
            Étape {activeStep + 1} sur {STEPS.length} — {STEPS[activeStep]}
          </Typography>
        ) : (
          <Stepper activeStep={activeStep} alternativeLabel sx={{ pt: 0.5, pb: 0.5 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Step content: isolated stacking context so nothing overlaps the DocuSign iframe */}
      <Box
        sx={{
          flexShrink: 0,
          width: '100%',
          isolation: 'isolate',
          position: 'relative',
          zIndex: 0,
        }}
      >
        {isDocusignStep && (
          <Paper
            variant="outlined"
            sx={{
              width: '100%',
              height: DOCUSIGN_IFRAME_HEIGHT,
              overflow: 'hidden',
              p: 0,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: '100%',
                overflow: 'hidden',
              }}
            >
              {renderDocusignPanel()}
            </Box>
          </Paper>
        )}

        {activeStep === 1 && renderMandateStep()}

        {activeStep === 2 && (
          <Alert severity="success">
            Votre contrat est signé et votre mandat SEPA est en place. Cliquez sur &quot;Suivant&quot; pour finaliser
            votre inscription.
          </Alert>
        )}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          pt: 1,
        }}
      >
        <Button
          variant="outlined"
          sx={{ height: { xs: 45 } }}
          startIcon={<MdNavigateBefore />}
          onClick={() => navigate(-1)}
        >
          Retour
        </Button>
        <Button
          variant="contained"
          sx={{ height: { xs: 45 } }}
          endIcon={<MdNavigateNext />}
          disabled={activeStep < 2}
          onClick={() => navigate({ pathname: './../recapitulatif/', search: location.search })}
        >
          Suivant
        </Button>
      </Box>
    </Box>
  )
}

export default GoCardless
